import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import { X, Download, FileText, User } from './icons';
import { asignacionesAPI, actasAPI, authAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import SignatureDrawer from './SignatureDrawer';

export default function GenerarActaModal({ isOpen, onClose, asignacion }) {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [includeSignature, setIncludeSignature] = useState(false);
    const [fechaEntrega, setFechaEntrega] = useState('');
    const [showSignatureDrawer, setShowSignatureDrawer] = useState(false);

    if (!isOpen || !asignacion) return null;

    const handleSaveSignature = async (file) => {
        try {
            const formData = new FormData();
            formData.append('signature', file);
            await authAPI.uploadSignature(formData);
            showToast('Éxito', 'Firma digital guardada correctamente', 'success');
            setShowSignatureDrawer(false);
            setTimeout(() => {
                handleGenerarActa();
            }, 300);
        } catch (err) {
            console.error('Error al guardar firma:', err);
            showToast('Error', 'No se pudo guardar la firma digital', 'error');
        }
    };

    const handleGenerarActa = async () => {
        if (!asignacion || !asignacion.id || asignacion.id === 0) {
            showToast('Error', 'No se ha seleccionado una asignación válida', 'error');
            return;
        }

        setLoading(true);
        try {
            let payload = {
                incluirFirmaTI: includeSignature,
                fechaEntrega: fechaEntrega || null,
                observaciones: null
            };

            // Determinar si es un movimiento de consumible o una asignación de activo
            if (asignacion.tipo === 'consumible' || (typeof asignacion.id === 'string' && asignacion.id.startsWith('mov_'))) {
                const movId = asignacion.originalId || parseInt(asignacion.id.replace('mov_', ''));
                payload.movimientoId = movId;
            } else {
                payload.asignacionId = asignacion.id;
            }

            // Usar el endpoint de previsualización temporal
            const response = await actasAPI.previsualizarActaTemporal(payload);

            // Abrir previsualización en el navegador
            const url = window.URL.createObjectURL(response.data);
            window.open(url, '_blank');

            onClose();
        } catch (error) {
            console.error('Error al previsualizar acta:', error);
            const errData = error.response?.data;
            const isNoSignature = errData?.code === 'NO_SIGNATURE' || (errData?.message && errData.message.toLowerCase().includes('firma'));

            if (isNoSignature || (includeSignature && error.response?.status === 400)) {
                showToast('Firma requerida', 'Debes registrar tu firma digital antes de generar el acta con firma TI.', 'warning');
                setShowSignatureDrawer(true);
            } else {
                showToast('Error', 'Error al previsualizar la acta. Por favor, inténtalo de nuevo.', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="relative top-20 mx-auto p-6 w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl transform transition-all">
                <div className="mt-1">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Generar Acta de Entrega
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {asignacion.activo?.codigo} → {asignacion.usuario?.nombre} {asignacion.usuario?.apellido}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Información de la asignación */}
                    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Usuario:</strong> {asignacion.usuario?.nombre} {asignacion.usuario?.apellido}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Activo:</strong> {asignacion.activo?.codigo} - {asignacion.activo?.categoria}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>Fecha de asignación:</strong> {new Date(asignacion.fechaAsignacion).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Opciones */}
                    <div className="space-y-5">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50">
                            <label className="flex items-start space-x-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={includeSignature}
                                    onChange={(e) => setIncludeSignature(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                    <span className="text-sm text-gray-700 dark:text-gray-300">
                                        Incluir firma digital del admin/soporte actual
                                    </span>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Se incluirá tu firma digital en la sección "Firma TI" del documento
                                    </p>
                                </div>
                            </label>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Fecha de entrega (opcional)
                            </label>
                            <input
                                type="date"
                                value={fechaEntrega}
                                onChange={(e) => setFechaEntrega(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Si no se especifica, se usará la fecha actual
                            </p>
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGenerarActa}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Generando...</span>
                                </>
                            ) : (
                                <>
                                    <FileText className="w-4 h-4" />
                                    <span>Previsualizar para Imprimir</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <SignatureDrawer
                isOpen={showSignatureDrawer}
                onClose={() => setShowSignatureDrawer(false)}
                onSave={handleSaveSignature}
                title="Crear tu firma digital para el acta"
            />
        </div>,
        document.body
    );
} 